import { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { Loader2 } from 'lucide-react';
import { Navigation } from '@/components/Navigation';
import { MatchSetup } from '@/components/game/MatchSetup';
import { GameHeader } from '@/components/game/GameHeader';
import { WikiArticleView } from '@/components/game/WikiArticleView';
import { ResultCard } from '@/components/game/ResultCard';
import { CompletionOverlay } from '@/components/game/CompletionOverlay';
import { AdSlot } from '@/components/ads/AdSlot';
import { useGame } from '@/hooks/useGame';
import { useAds } from '@/hooks/useAds';
import { useAuth } from '@/hooks/useAuth';
import { Match, Run } from '@/types/game';
import { supabase } from '@/integrations/supabase/client';
import { toast } from 'sonner';
import { trackEvent } from '@/lib/analytics';

export default function PlayPage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { user } = useAuth();
  const { shouldShowAd, incrementMatchCount, resetAdCounter } = useAds();
  const {
    gameState,
    currentArticle,
    isLoading,
    error,
    elapsedTime,
    startGame,
    navigateToArticle,
    giveUp,
    restartGame,
    saveRun,
  } = useGame();
  const [completedRun, setCompletedRun] = useState<Run | null>(null);
  const [compareRun, setCompareRun] = useState<Run | null>(null);
  const [showSetup, setShowSetup] = useState(true);
  const [preloadedMatch, setPreloadedMatch] = useState<Match | null>(null);
  const [showCompletionOverlay, setShowCompletionOverlay] = useState(false);

  // Handle URL params for beat/daily/challenge
  useEffect(() => {
    const beatRunId = searchParams.get('beat');
    const matchId = searchParams.get('matchId');
    const daily = searchParams.get('daily');

    if (beatRunId) {
      loadBeatRun(beatRunId);
    } else if (matchId) {
      loadMatch(matchId);
    }
  }, [searchParams]);

  const loadBeatRun = async (runId: string) => {
    try {
      const { data, error } = await supabase
        .from('runs')
        .select('*, match:matches(*)')
        .eq('id', runId)
        .single();

      if (error || !data) {
        toast.error('Run not found');
        return;
      }

      const run = { ...data, path: data.path as string[] || [] } as Run & { match: Match };
      setCompareRun(run);
      setPreloadedMatch(data.match as Match);
      setShowSetup(false);
      await startGame(data.match as Match);
    } catch (err) {
      console.error('Error loading beat run:', err);
    }
  };

  const loadMatch = async (matchId: string) => {
    try {
      const { data, error } = await supabase
        .from('matches')
        .select('*')
        .eq('id', matchId)
        .single();

      if (error || !data) {
        toast.error('Match not found');
        return;
      }

      setPreloadedMatch(data as Match);
      setShowSetup(false);
      await startGame(data as Match);
    } catch (err) {
      console.error('Error loading match:', err);
    }
  };

  // Handle game completion - show overlay for wins
  useEffect(() => {
    if (gameState.isCompleted && !completedRun) {
      const durationMs = gameState.startTime ? Date.now() - gameState.startTime : 0;
      
      // Track analytics
      if (gameState.match) {
        trackEvent.playComplete(
          gameState.match.id,
          durationMs,
          gameState.clicks,
          true
        );
      }
      
      saveRun().then((run) => {
        setCompletedRun(run);
        setShowCompletionOverlay(true);
        if (run && gameState.match) {
          trackEvent.runCompleted(run.id, gameState.match.id, run.is_public);
        }
      });
      incrementMatchCount();
    } else if (gameState.isFailed && !completedRun) {
      const durationMs = gameState.startTime ? Date.now() - gameState.startTime : 0;
      
      // Track analytics for failure
      if (gameState.match) {
        trackEvent.playComplete(
          gameState.match.id,
          durationMs,
          gameState.clicks,
          false
        );
      }
      
      saveRun().then((run) => {
        setCompletedRun(run);
        if (run && gameState.match) {
          trackEvent.runCompleted(run.id, gameState.match.id, run.is_public);
        }
      });
      incrementMatchCount();
    }
  }, [gameState.isCompleted, gameState.isFailed]);

  const handleStartMatch = async (match: Match) => {
    // Track analytics
    trackEvent.playStart(match.id, match.mode, match.difficulty);
    
    setShowSetup(false);
    setCompletedRun(null);
    setCompareRun(null);
    setShowCompletionOverlay(false);
    if (shouldShowAd) resetAdCounter();
    await startGame(match);
  };

  const handlePlayAgain = () => {
    setShowSetup(true);
    setCompletedRun(null);
    setCompareRun(null);
    setPreloadedMatch(null);
    setShowCompletionOverlay(false);
    // Clear URL params
    navigate('/play', { replace: true });
  };

  const handleGoHome = () => {
    navigate('/');
  };

  const handleDismissOverlay = () => {
    setShowCompletionOverlay(false);
  };

  // Setup screen
  if (showSetup) {
    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-8">
          <MatchSetup onStartMatch={handleStartMatch} showAd={shouldShowAd} />
        </main>
      </div>
    );
  }

  // Result screen (after dismissing overlay or for failures)
  // Show result if game ended AND (we have completedRun OR game just failed and waiting for save)
  const showResult = (gameState.isCompleted || gameState.isFailed) && !showCompletionOverlay;
  
  if (showResult) {
    // Show loading state while waiting for run to save
    if (!completedRun) {
      return (
        <div className="min-h-screen bg-background">
          <Navigation />
          <main className="container mx-auto px-4 py-8 flex items-center justify-center">
            <div className="flex flex-col items-center gap-4">
              <Loader2 className="w-8 h-8 animate-spin text-primary" />
              <p className="text-muted-foreground">Saving result...</p>
            </div>
          </main>
        </div>
      );
    }

    return (
      <div className="min-h-screen bg-background">
        <Navigation />
        <main className="container mx-auto px-4 py-8">
          {shouldShowAd && (
            <div className="max-w-lg mx-auto mb-6">
              <AdSlot placement="result" />
            </div>
          )}
          <ResultCard
            run={completedRun}
            match={gameState.match!}
            onPlayAgain={handlePlayAgain}
            onGoHome={handleGoHome}
            onGoToProfile={user ? () => navigate('/profile') : undefined}
            compareRun={compareRun}
          />
        </main>
      </div>
    );
  }

  // Game screen
  if (gameState.isPlaying && gameState.match) {
    return (
      <div className="min-h-screen bg-background flex flex-col">
        <GameHeader
          match={gameState.match}
          currentArticle={gameState.currentArticle}
          clicks={gameState.clicks}
          elapsedTime={elapsedTime}
          onGiveUp={giveUp}
          onRestart={restartGame}
          isLoading={isLoading}
        />
        <main className="flex-1 overflow-hidden">
          <WikiArticleView
            article={currentArticle}
            isLoading={isLoading}
            error={error}
            onLinkClick={navigateToArticle}
          />
        </main>
        
        {/* Completion overlay for wins */}
        {showCompletionOverlay && completedRun && gameState.isCompleted && (
          <CompletionOverlay
            run={completedRun}
            match={gameState.match}
            onPlayAgain={handlePlayAgain}
            onGoHome={handleGoHome}
            onDismiss={handleDismissOverlay}
          />
        )}
      </div>
    );
  }

  // Fallback - should not reach here normally
  return (
    <div className="min-h-screen bg-background">
      <Navigation />
      <main className="container mx-auto px-4 py-8 flex items-center justify-center">
        <p className="text-muted-foreground">Loading...</p>
      </main>
    </div>
  );
}